import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchUserFavoritesAPI,
  fetchUserPreferencesAPI,
  updateUserFavoritesAPI,
} from 'src/services/api/userPreferencesService';
import {
  CreateOrUpdateUserFavoritesInput,
  FavoriteAction,
  FavoriteType,
  MutationResponse,
  ToggleUserFavoriteInput,
  UpdateUserFavoritesResponse,
  UserFavoritesResponse,
  UserPreferencesDetails,
} from '../../types/userPreferences/userPreferences.types';
import { RootState } from '../store';

// Define the state interface
interface UserPreferencesState {
  preferences: UserPreferencesDetails | null;
  favorites: UserFavoritesResponse | null;
  isLoadingPreferences: boolean;
  isLoadingFavorites: boolean;
  preferencesError: string | null;
  favoritesError: string | null;
  hasAttemptedPreferencesFetch: boolean;
  hasAttemptedFavoritesFetch: boolean;
  isTogglingFavorite: boolean;
  toggleFavoriteError: string | null;
}

// Initial state
const initialState: UserPreferencesState = {
  preferences: null,
  favorites: null,
  isLoadingPreferences: false,
  isLoadingFavorites: false,
  preferencesError: null,
  favoritesError: null,
  hasAttemptedPreferencesFetch: false,
  hasAttemptedFavoritesFetch: false,
  isTogglingFavorite: false,
  toggleFavoriteError: null,
};

// Async thunk actions
/**
 * Fetch user preferences details
 */
export const fetchUserPreferences = createAsyncThunk(
  'userPreferences/fetchUserPreferences',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchUserPreferencesAPI();
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch user preferences');
    }
  }
);

/**
 * Fetch user favorites details
 * Returns UserFavoritesResponse with product_line_ids, rpn_ids, fpn_ids
 */
export const fetchUserFavorites = createAsyncThunk<UserFavoritesResponse, void, { rejectValue: string }>(
  'userPreferences/fetchUserFavorites',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchUserFavoritesAPI();
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch user favorites');
    }
  }
);

/**
 * Toggle a user favorite (add or remove)
 * Computes the new favorites state and sends the entire state to the backend
 */
export const toggleUserFavorite = createAsyncThunk<
  UpdateUserFavoritesResponse,
  ToggleUserFavoriteInput,
  { rejectValue: string; state: RootState }
>('userPreferences/toggleUserFavorite', async (input, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const currentFavorites = state.userPreferences.favorites;

    // Determine action based on current favorite status
    const action: FavoriteAction = input.isFavorited ? FavoriteAction.REMOVED : FavoriteAction.ADDED;

    // Compute the new favorites state
    // If favorites haven't been loaded yet, start with empty arrays
    const newFavorites: CreateOrUpdateUserFavoritesInput = {
      product_line_ids: currentFavorites ? [...currentFavorites.product_line_ids] : [],
      rpn_ids: currentFavorites ? [...currentFavorites.rpn_ids] : [],
      fpn_ids: currentFavorites ? [...currentFavorites.fpn_ids] : [],
    };

    // Update the appropriate array based on favorite type and action
    if (action === FavoriteAction.ADDED) {
      switch (input.favorite_type) {
        case FavoriteType.PRODUCT_LINE:
          if (!newFavorites.product_line_ids!.includes(input.item_id)) {
            newFavorites.product_line_ids!.push(input.item_id);
          }
          break;
        case FavoriteType.RPN:
          if (!newFavorites.rpn_ids!.includes(input.item_id)) {
            newFavorites.rpn_ids!.push(input.item_id);
          }
          break;
        case FavoriteType.FPN:
          if (!newFavorites.fpn_ids!.includes(input.item_id)) {
            newFavorites.fpn_ids!.push(input.item_id);
          }
          break;
      }
    } else {
      switch (input.favorite_type) {
        case FavoriteType.PRODUCT_LINE:
          newFavorites.product_line_ids = newFavorites.product_line_ids!.filter((id) => id !== input.item_id);
          break;
        case FavoriteType.RPN:
          newFavorites.rpn_ids = newFavorites.rpn_ids!.filter((id) => id !== input.item_id);
          break;
        case FavoriteType.FPN:
          newFavorites.fpn_ids = newFavorites.fpn_ids!.filter((id) => id !== input.item_id);
          break;
      }
    }

    // Call the API with the complete new favorites state
    const result: MutationResponse = await updateUserFavoritesAPI(newFavorites);

    // If successful, return the response for the reducer to update state
    const recordsUpdated = result.numberOfRecordsUpdated ?? -1;
    if (recordsUpdated >= 0) {
      return {
        success: true,
        action,
        favorite_type: input.favorite_type,
        item_id: input.item_id,
      };
    }

    throw new Error('Failed to update favorites');
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Failed to toggle user favorite');
  }
});

/**
 * Update user preferences
 * TODO: Implement API call once service is created
 */
export const updateUserPreferences = createAsyncThunk<
  UserPreferencesDetails,
  UserPreferencesDetails,
  { rejectValue: string }
>('userPreferences/updateUserPreferences', async (_preferences, { rejectWithValue }) => {
  try {
    // TODO: Replace with actual API call
    // return await updateUserPreferencesAPI(preferences);
    throw new Error('API call not yet implemented');
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Failed to update user preferences');
  }
});

// Create the slice
const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    setPreferences: (state, action: PayloadAction<UserPreferencesDetails | null>) => {
      state.preferences = action.payload;
    },
    setFavorites: (state, action: PayloadAction<UserFavoritesResponse | null>) => {
      state.favorites = action.payload;
    },
    clearPreferencesError: (state) => {
      state.preferencesError = null;
    },
    clearFavoritesError: (state) => {
      state.favoritesError = null;
    },
    clearToggleFavoriteError: (state) => {
      state.toggleFavoriteError = null;
    },
    clearAllErrors: (state) => {
      state.preferencesError = null;
      state.favoritesError = null;
      state.toggleFavoriteError = null;
    },
    reset: () => initialState,
    resetPreferencesFetchStatus: (state) => {
      state.hasAttemptedPreferencesFetch = false;
    },
    resetFavoritesFetchStatus: (state) => {
      state.hasAttemptedFavoritesFetch = false;
    },
    /**
     * Optimistically update favorites in state after toggle
     * Used when we want to update UI immediately before API confirms
     */
    optimisticToggleFavorite: (state, action: PayloadAction<ToggleUserFavoriteInput & { action: FavoriteAction }>) => {
      if (!state.favorites) return;

      const { favorite_type, item_id, action: favoriteAction } = action.payload;

      if (favoriteAction === FavoriteAction.ADDED) {
        // Add to appropriate array
        switch (favorite_type) {
          case 'PRODUCT_LINE':
            if (!state.favorites.product_line_ids.includes(item_id)) {
              state.favorites.product_line_ids.push(item_id);
            }
            break;
          case 'RPN':
            if (!state.favorites.rpn_ids.includes(item_id)) {
              state.favorites.rpn_ids.push(item_id);
            }
            break;
          case 'FPN':
            if (!state.favorites.fpn_ids.includes(item_id)) {
              state.favorites.fpn_ids.push(item_id);
            }
            break;
        }
      } else {
        // Remove from appropriate array
        switch (favorite_type) {
          case 'PRODUCT_LINE':
            state.favorites.product_line_ids = state.favorites.product_line_ids.filter((id) => id !== item_id);
            break;
          case 'RPN':
            state.favorites.rpn_ids = state.favorites.rpn_ids.filter((id) => id !== item_id);
            break;
          case 'FPN':
            state.favorites.fpn_ids = state.favorites.fpn_ids.filter((id) => id !== item_id);
            break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchUserPreferences
      .addCase(fetchUserPreferences.pending, (state) => {
        state.isLoadingPreferences = true;
        state.preferencesError = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.isLoadingPreferences = false;
        state.preferences = action.payload;
        state.hasAttemptedPreferencesFetch = true;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.isLoadingPreferences = false;
        state.preferencesError = action.payload as string;
        state.hasAttemptedPreferencesFetch = true;
      })
      // fetchUserFavorites
      .addCase(fetchUserFavorites.pending, (state) => {
        state.isLoadingFavorites = true;
        state.favoritesError = null;
      })
      .addCase(fetchUserFavorites.fulfilled, (state, action) => {
        state.isLoadingFavorites = false;
        state.favorites = action.payload;
        state.hasAttemptedFavoritesFetch = true;
      })
      .addCase(fetchUserFavorites.rejected, (state, action) => {
        state.isLoadingFavorites = false;
        state.favoritesError = action.payload as string;
        state.hasAttemptedFavoritesFetch = true;
      })
      // toggleUserFavorite
      .addCase(toggleUserFavorite.pending, (state) => {
        state.isTogglingFavorite = true;
        state.toggleFavoriteError = null;
      })
      .addCase(toggleUserFavorite.fulfilled, (state, action) => {
        state.isTogglingFavorite = false;
        // Update favorites based on response
        if (state.favorites && action.payload.success) {
          const { favorite_type, item_id, action: favoriteAction } = action.payload;
          if (favoriteAction === FavoriteAction.ADDED) {
            switch (favorite_type) {
              case FavoriteType.PRODUCT_LINE:
                if (!state.favorites.product_line_ids.includes(item_id)) {
                  state.favorites.product_line_ids.push(item_id);
                }
                break;
              case FavoriteType.RPN:
                if (!state.favorites.rpn_ids.includes(item_id)) {
                  state.favorites.rpn_ids.push(item_id);
                }
                break;
              case FavoriteType.FPN:
                if (!state.favorites.fpn_ids.includes(item_id)) {
                  state.favorites.fpn_ids.push(item_id);
                }
                break;
            }
          } else {
            switch (favorite_type) {
              case FavoriteType.PRODUCT_LINE:
                state.favorites.product_line_ids = state.favorites.product_line_ids.filter((id) => id !== item_id);
                break;
              case FavoriteType.RPN:
                state.favorites.rpn_ids = state.favorites.rpn_ids.filter((id) => id !== item_id);
                break;
              case FavoriteType.FPN:
                state.favorites.fpn_ids = state.favorites.fpn_ids.filter((id) => id !== item_id);
                break;
            }
          }
        }
      })
      .addCase(toggleUserFavorite.rejected, (state, action) => {
        state.isTogglingFavorite = false;
        state.toggleFavoriteError = action.payload as string;
      })
      // updateUserPreferences
      .addCase(updateUserPreferences.pending, (state) => {
        state.isLoadingPreferences = true;
        state.preferencesError = null;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.isLoadingPreferences = false;
        state.preferences = action.payload;
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.isLoadingPreferences = false;
        state.preferencesError = action.payload as string;
      });
  },
});

// Export actions
export const {
  setPreferences,
  setFavorites,
  clearPreferencesError,
  clearFavoritesError,
  clearToggleFavoriteError,
  clearAllErrors,
  reset,
  resetPreferencesFetchStatus,
  resetFavoritesFetchStatus,
  optimisticToggleFavorite,
} = userPreferencesSlice.actions;

// Export reducer
export default userPreferencesSlice.reducer;
